import { ResponseValueType, PatchedResponse } from './types'

export type ResponseParser = (response: Response) => Promise<PatchedResponse>

const JSON_MIME_TYPES = ['application/json', 'application/problem+json']

export class RetryError extends Error {}

/**
 * We need to do this because the response is already present from `isomorphic-fetch` and we
 * can overwrite it in a node environment, but not in a browser.
 */
const setResponseBody = (response: Response, value: ResponseValueType) => {
  Object.defineProperty(response, 'body', {
    value,
    enumerable: true,
  })
}

/**
 * These functions are responsible for accepting a response from native fetch, and parsing it into
 * an appropriate format
 */
const responseParsers = {
  text: (response: Response) => {
    return response.text().then((text: string) => {
      setResponseBody(response, text)

      return Promise.resolve(response)
    })
  },

  blob: (response: Response) => {
    return response.blob().then((blob: Blob) => {
      setResponseBody(response, blob)

      return Promise.resolve(response)
    })
  },

  arrayBuffer: (response: Response) => {
    return response.arrayBuffer().then((buffer: ArrayBuffer) => {
      setResponseBody(response, buffer)

      return Promise.resolve(response)
    })
  },

  formData: (response: Response) => {
    return response.formData().then((data: FormData) => {
      setResponseBody(response, data)

      return Promise.resolve(response)
    })
  },

  /**
   * Do not parse the response if client needs the raw stream such as reading a file
   * the text parser attempts to convert raw data into a string, which could transform binary data incorrectly for a binary stream
   *
   * Non-spec
   */
  rawStream: (response: Response) => {
    return Promise.resolve(response).then((data: any) => {
      setResponseBody(response, data.body)
      return Promise.resolve(response)
    })
  },

  /**
   * Returns a buffer type if it's possible, else falls back to arrayBuffer defined by the spec. This is mainly to keep
   * the interface isomorphic. This is mainly for use in the server, but won't blow up if used on the client, just
   * becoming an alias for another well supported buffer format in that environment.
   *
   * Non-spec
   */
  buffer: (response: Response) => {
    // This is a feature well-supported in node-fetch, particularly useful for binary formats with a set content-type,
    // like streaming excel files
    const formatResolver =
      // @ts-ignore
      'buffer' in response ? response.buffer() : response.arrayBuffer()

    return formatResolver.then((data: any) => {
      setResponseBody(response, data)

      return Promise.resolve(response)
    })
  },

  /**
   * Even though this is called json, it also handles plain text responses. The reason we mix the
   * two is because we want to add a few sanity guards by default, like avoiding trying to
   * JSON.parse an empty response body.
   */
  json: (response: Response) => {
    // NOTE: Should be case insensitive https://www.w3.org/Protocols/rfc2616/rfc2616-sec4.html#sec4.2
    var contentType = response.headers.get('content-type') || ''

    // Don't use .json in case response length is 0, which is very common for 204 responses
    return response
      .text()
      .then(function (text: string) {
        return text &&
          text.length &&
          // We use .some() and .includes() here instead of exact equality checking because we want to be able to
          // handle values like `application/json; charset=utf8`.
          JSON_MIME_TYPES.some(mimeType => contentType.includes(mimeType))
          ? JSON.parse(text)
          : text
      })
      .then(function (parsedBody: Record<string, string> | string) {
        setResponseBody(response, parsedBody)

        return Promise.resolve(response)
      })
  },
}

export default responseParsers
